// import { isEqual } from 'lodash'

import Ngql from 'gql/ninegql'

import { restLandingClient } from 'myNet'
import { i18Key, getCid } from 'utils'
import { truncateSignatureTitle } from 'utils/signatureTitle'
import PostalMime from 'postal-mime'

import NQuery from '../query'

import listInfo from '../query/table-info/signature'

// Type definitions
interface AppConfigResponse {
  data: {
    data: {
      appconfig: AppConfigItem[]
    }
    code: number
  }
}

interface AppConfigItem {
  default_appconfig: boolean
  UserSignature: string
  [key: string]: any
}

interface SignatureData {
  signatureId: string
  default: boolean
  title?: string
  titleKey?: string
  content: string
  editable: boolean
}

interface UserInfo {
  email: string
  [key: string]: any
}

interface FakeReactObjState {
  [key: string]: any
}

interface ReactObj {
  setState: (state: any) => void
  [key: string]: any
}

interface GQLSignatureNode {
  node: {
    signature: {
      id: string
      title: string
      content: string
    }
    default: boolean
  }
}

interface SignatureResponse {
  data: {
    allUserSignatures: {
      edges: GQLSignatureNode[]
    }
  }
}

interface AttachmentInfo {
  contentId: string
  [key: string]: any
}

interface ParsedContent {
  content: string
  attachments: AttachmentInfo[]
}

export const getAppConfig = async (email: string): Promise<AppConfigResponse> => {
  return await restLandingClient.post('/provisioning/appconfig', {
    email,
  })
}

class FakeReactObj {
  APIObj: SignatureAPI
  state: FakeReactObjState

  constructor(APIObj: SignatureAPI) {
    this.APIObj = APIObj
    this.state = {}
  }

  setState = (newState: FakeReactObjState): void => {
    // if (isEqual(this.state, newState) === true) {
    //   return;
    // }
    this.state = { ...this.state, ...newState }
    // 렌더링 처리.
    const reactObj = this.APIObj.getReactObj()
    if (reactObj != null) {
      if (typeof reactObj === 'function') {
        reactObj(this.state)
      } else {
        reactObj.setState(this.state)
      }
    }
  }

  isMounted = (): boolean => {
    return true
  }

  updater = {
    isMounted: this.isMounted,
  }
}

class SignatureAPI {
  isFirstLoaded: boolean
  signatureList: SignatureData[]
  signaturesFromAppConfig: SignatureData[]
  signaturesFromSignature: SignatureData[]
  fakeReactObj: FakeReactObj | null
  signatureObj: any
  listObj: any
  firstPromise: Promise<SignatureData[]> | null
  reactObj: ReactObj | ((state: any) => void) | null
  userInfo: UserInfo | null

  constructor() {
    this.isFirstLoaded = false
    this.signatureList = []
    this.signaturesFromAppConfig = []
    this.signaturesFromSignature = []
    this.fakeReactObj = null
    this.signatureObj = null
    this.listObj = null
    this.firstPromise = null
    this.reactObj = null
    this.userInfo = null
  }

  private processRes = (res: SignatureResponse): void => {
    console.log(' ----- res : ', res)
    const { allUserSignatures } = res.data
    const { edges } = allUserSignatures
    if (edges != null) {
      this.signaturesFromSignature = edges.map((edge) => {
        const { node } = edge
        const { signature, default: isDefault } = node
        const { id, title, content } = signature

        // 방어 레이어: 백엔드에 50자를 초과하는 제목이 저장돼 있어도 목록/삽입 시 50자로 잘라 표시한다.
        // (appConfig 서명은 titleKey 기반이라 대상 아님)
        const { title: safeTitle } = truncateSignatureTitle(title)

        return {
          signatureId: id,
          default: isDefault,
          editable: true,
          title: safeTitle,
          content,
        }
      })
      this.signatureList = [...this.signaturesFromAppConfig, ...this.signaturesFromSignature]

      if (this.listObj != null) {
        this.listObj.setData(this.signatureList)
      }
    }
  }

  open = async (): Promise<SignatureData[]> => {
    this.close()

    this.signatureList = []
    this.signaturesFromAppConfig = []
    this.signaturesFromSignature = []

    this.fakeReactObj = new FakeReactObj(this)

    this.signatureObj = Ngql.GQLObj(NQuery.querySignatureList as any, { useUI: false })

    this.listObj = Ngql.getMemTableObj(
      this.signatureList,
      {
        name: 'list',
        pageParamName: 'pn1',
        tableInfo: listInfo,
      },
      this.fakeReactObj,
    )

    this.firstPromise = this.firstLoad()

    return await this.firstPromise

    // .result().then((res) => {
    //   console.log(' -- signature 1 watch result : ', res)
    // });
  }

  firstLoad = (): Promise<SignatureData[]> => {
    try {
      const promise1 = this.getSignatureFromAppConfig()
      const promise2 = new Promise<SignatureData[]>((resolve) => {
        this.signatureObj.watch((res: SignatureResponse) => {
          this.processRes(res)
          resolve(this.signatureList)
        })
      })

      return Promise.all([promise1, promise2]).then(() => {
        this.isFirstLoaded = true
        this.firstPromise = null
        return this.signatureList
      })
    } catch {
      this.isFirstLoaded = true
      this.firstPromise = null
      return Promise.resolve([])
    }
  }

  close = (): void => {
    if (this.listObj != null && this.listObj.close != null) {
      this.listObj.close()
      this.listObj = null
    }
    this.isFirstLoaded = false
  }

  reloadList = (forced: boolean = false): Promise<SignatureData[]> => {
    if (this.signatureObj != null) {
      if (this.firstPromise != null && forced !== true) {
        return this.firstPromise
      }

      const promise1 = this.getSignatureFromAppConfig()
      const promise2 = new Promise<SignatureData[]>((resolve) => {
        this.signatureObj.refetch().then((res: SignatureResponse) => {
          this.processRes(res)
          resolve(this.signatureList)
        })
      })

      return Promise.all([promise1, promise2]).then(() => {
        return this.signatureList
      })
    }

    return Promise.resolve([])
  }

  getReactObj = (): ReactObj | ((state: any) => void) | null => {
    return this.reactObj
  }

  setReactObj = (reactObj: ReactObj | ((state: any) => void) | null): void => {
    this.reactObj = reactObj
  }

  getList = (): SignatureData[] => {
    if (this.listObj == null) {
      return []
    }

    return this.signatureList
  }

  getElement = (): any => {
    if (this.listObj != null) {
      const tableElem = this.listObj.getArrayElem({
        classNames: ['signatureList'],
      })

      return tableElem
    }

    return null
  }

  createSignature = (title: string, content: string): Promise<any> => {
    if (this.listObj == null) {
      return Promise.resolve(null)
    }

    // console.log(' -- createSignature -- ', title, content)
    const signatureData = {
      title,
      content,
    }

    return Ngql.GQLObj(NQuery.mutationCreateSignature as any, {
      useUI: false,
    }).mutate({
      variables: {
        signatureData,
      },
    })
  }

  updateSignature = (signatureGid: string, title: string, content: string): Promise<any> => {
    if (this.listObj == null) {
      return Promise.resolve(null)
    }

    // console.log(' -- updateSignature -- ', title, content, signatureGid)
    const signatureData = {
      title,
      content,
    }
    return Ngql.GQLObj(NQuery.mutationUpdateSignature as any, {
      useUI: false,
    }).mutate({
      variables: {
        signatureData,
        signatureGid,
      },
    })
  }

  deleteSignatures = (signatureGids: string[]): Promise<any> => {
    if (this.listObj == null) {
      return Promise.resolve(null)
    }

    // console.log(' -- deleteSignatures -- ', signatureGids)
    return Ngql.GQLObj(NQuery.mutationDeleteSignature as any, {
      useUI: false,
    }).mutate({
      variables: {
        signatureGids,
      },
    })
  }

  setDefaultSignature = (signatureGid: string, isDefault: boolean): Promise<any> => {
    if (this.listObj == null) {
      return Promise.resolve(null)
    }

    return Ngql.GQLObj(NQuery.mutationDefaultSignature as any, {
      useUI: false,
    }).mutate({
      variables: { signatureGid, isDefault },
    })
  }

  setUserInfo = (userInfo: UserInfo): void => {
    this.userInfo = userInfo
  }

  getSignatureFromAppConfig = (): Promise<string | null> => {
    // console.log(' -- onClick10 -- ')
    // Ngql.GQLObj(NQuery.queryAppConfigList, {
    //   useUI: false,
    // }).query({}).then((res) => {
    //   console.log(' ---- queryAppConfigList : ', res)
    // })

    if (this.listObj == null) {
      return Promise.resolve(null)
    }

    if (this.userInfo == null) {
      return Promise.resolve(null)
    }

    const email = this.userInfo.email

    return getAppConfig(email).then((res) => {
      let signature: string | null = null

      console.log(' res - ', res)
      const { data, code } = res.data
      if (code === 200) {
        const { appconfig } = data
        const defaultAppConfig = appconfig.filter((item) => {
          return item.default_appconfig === true
        })

        if (defaultAppConfig.length > 0) {
          // console.log(' ---- default appConfig : ', defaultAppConfig.length, defaultAppConfig[0])
          // console.log(' ---- defalut signature :', defaultAppConfig[0].UserSignature)
          signature = defaultAppConfig[0].UserSignature

          const signatureObj: SignatureData = {
            signatureId: 'default',
            default: false,
            // title: 'Signature',
            titleKey: i18Key('editor.signature.default'),
            content: signature,
            editable: false,
          }

          this.signaturesFromAppConfig = [signatureObj]

          this.signatureList = [...this.signaturesFromAppConfig, ...this.signaturesFromSignature]

          this.listObj.setData(this.signatureList)
        }
      }
      return signature
    })
  }

  parseContent = async (content: string): Promise<ParsedContent | null> => {
    let ret: ParsedContent | null = null
    try {
      const postalMimeEML = await PostalMime.parse(content)
      // console.log(' - postalMimeEML - ', postalMimeEML)
      if (postalMimeEML.html != null) {
        const attachments = (postalMimeEML.attachments || []).map((attachment: any) => {
          const { contentId } = attachment
          const cid = getCid(contentId)
          attachment.contentId = cid
          return attachment
        })
        ret = {
          content: postalMimeEML.html,
          attachments,
        }
      }
    } catch (error) {
      console.log(' - PostalMime parseerror - ', error)
    }

    return ret
  }
}

const signatureAPI = new SignatureAPI()

export default signatureAPI
